import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://core.gamura.ai";

const getAuthToken = () => localStorage.getItem("gamora_token");

const api = axios.create({
	baseURL: API_BASE_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Add a request interceptor to include Authorization header
api.interceptors.request.use((config) => {
	const token = getAuthToken();
	if (token) {
		config.headers = config.headers || {};
		config.headers["Authorization"] = `Bearer ${token}`;
	}
	return config;
});

// Organization Types
export interface Organization {
	id: string;
	name: string;
	description?: string;
	domain?: string;
	logo_url?: string;
	website?: string;
	industry?: string;
	size?: string;
	created_at: string;
	updated_at: string;
	member_count?: number;
	user_role?: string;
}

export interface OrganizationMember {
	id: string;
	user_id: string;
	organization_id: string;
	role: OrganizationRole | string | number;
	joined_at: string;
	user: {
		id: string;
		username: string;
		email: string;
		company?: string;
	};
}

export interface OrganizationInvitation {
	id: string;
	organization_id?: string; // Optional since it might come from organization object
	email: string;
	role: OrganizationRole | string | number; // Role they will have after accepting (Admin or Member)
	status: InvitationStatus | string | number;
	created_at: string;
	expires_at: string;
	updated_at?: string;
	organization?: {
		id: string;
		name: string;
		description?: string;
		logo_url?: string;
		website?: string;
		industry?: string;
	};
}

export interface OrganizationCreateRequest {
	name: string;
	description?: string;
	domain?: string;
	logo_url?: string;
	website?: string;
	industry?: string;
	size?: string;
}

export interface OrganizationUpdateRequest {
	name?: string;
	description?: string;
	domain?: string;
	logo_url?: string;
	website?: string;
	industry?: string;
	size?: string;
}

export interface InvitationCreateRequest {
	email: string;
	role: OrganizationRole | string | number; // Role they will have after accepting (Admin or Member)
	message?: string;
}

export interface MemberRoleUpdateRequest {
	role: OrganizationRole | string | number;
}

export enum OrganizationRole {
	OWNER = 0,
	ADMIN = 10,
	MEMBER = 20,
	VIEWER = 30,
}

export enum InvitationStatus {
	PENDING = 0,
	ACCEPTED = 1,
	DECLINED = 2,
	EXPIRED = 3,
}

// Organization API Functions
export const organizationApi = {
	// Get all organizations for the current user
	getMyOrganizations: async (): Promise<Organization[]> => {
		const response = await api.get("/api/v1/organizations/");
		return response.data;
	},

	// Get organization details
	getOrganization: async (orgId: string): Promise<Organization> => {
		const response = await api.get(`/organizations/${orgId}`);
		return response.data;
	},

	// Create new organization
	createOrganization: async (
		data: OrganizationCreateRequest
	): Promise<Organization> => {
		const response = await api.post("/api/v1/organizations", data);
		return response.data;
	},

	// Update organization (owner only)
	updateOrganization: async (
		orgId: string,
		data: OrganizationUpdateRequest
	): Promise<Organization> => {
		const response = await api.put(`/organizations/${orgId}`, data);
		return response.data;
	},

	// Delete organization (owner only)
	deleteOrganization: async (orgId: string): Promise<void> => {
		await api.delete(`/organizations/${orgId}`);
	},

	// Get organization members
	getOrganizationMembers: async (
		orgId: string
	): Promise<OrganizationMember[]> => {
		const response = await api.get(`/organizations/${orgId}/members`);
		return response.data;
	},

	// Create invitation (owner only)
	createInvitation: async (
		orgId: string,
		data: InvitationCreateRequest
	): Promise<OrganizationInvitation> => {
		const response = await api.post(
			`/organizations/${orgId}/invitations`,
			data
		);
		return response.data;
	},

	// Get organization invitations (owner only)
	getOrganizationInvitations: async (
		orgId: string
	): Promise<OrganizationInvitation[]> => {
		const response = await api.get(`/organizations/${orgId}/invitations`);
		return response.data;
	},

	// Remove member (owner only)
	removeMember: async (orgId: string, userId: string): Promise<void> => {
		await api.delete(`/organizations/${orgId}/members/${userId}`);
	},

	// Update member role (owner only)
	updateMemberRole: async (
		orgId: string,
		userId: string,
		data: MemberRoleUpdateRequest
	): Promise<void> => {
		await api.put(`/organizations/${orgId}/members/${userId}/role`, data);
	},

	// Transfer ownership (owner only)
	transferOwnership: async (
		orgId: string,
		newOwnerId: string
	): Promise<void> => {
		await api.post(`/organizations/${orgId}/transfer-ownership`, {
			new_owner_id: newOwnerId,
		});
	},

	// Accept invitation
	acceptInvitation: async (invitationId: string): Promise<void> => {
		await api.post(`/organizations/invitations/${invitationId}/accept`);
	},

	// Decline invitation
	declineInvitation: async (invitationId: string): Promise<void> => {
		await api.post(`/organizations/invitations/${invitationId}/decline`);
	},

	// Get invitation details by token
	getInvitationDetails: async (token: string): Promise<any> => {
		const response = await api.get(
			`/organizations/invitations/${token}/details`
		);
		return response;
	},

	// Get pending invitations for current user
	getMyInvitations: async (): Promise<OrganizationInvitation[]> => {
		try {
			const response = await api.get("/api/v1/organizations/invitations/my");
			return response.data;
		} catch (error) {
			console.error("Failed to fetch user invitations:", error);
			// Return empty array if API call fails
			return [];
		}
	},
};

// Permission helper functions
export const hasPermission = (
	userRole: OrganizationRole | string | number,
	requiredRole: OrganizationRole | string | number
): boolean => {
	const role1 = typeof userRole === "string" ? parseInt(userRole) : userRole;
	const role2 =
		typeof requiredRole === "string" ? parseInt(requiredRole) : requiredRole;
	return role1 <= role2;
};

export const isOwner = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleEqual(userRole, OrganizationRole.OWNER);
};

export const isAdmin = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleGreaterOrEqual(OrganizationRole.ADMIN, userRole);
};

export const canManageMembers = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleEqual(userRole, OrganizationRole.OWNER);
};

// New permission functions for viewing (not managing)
export const canViewMembers = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleGreaterOrEqual(OrganizationRole.MEMBER, userRole); // OWNER, ADMIN, MEMBER can view
};

export const canViewInvitations = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleGreaterOrEqual(OrganizationRole.ADMIN, userRole); // OWNER and ADMIN can view
};

export const canDeleteOrganization = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleEqual(userRole, OrganizationRole.OWNER);
};

export const canUpdateOrganization = (
	userRole: OrganizationRole | string | number
): boolean => {
	return isRoleEqual(userRole, OrganizationRole.OWNER);
};

export const getRoleLabel = (
	role: OrganizationRole | string | number
): string => {
	// Handle string and numeric role values
	const roleValue = typeof role === "string" ? parseInt(role) : role;

	switch (roleValue) {
		case OrganizationRole.OWNER:
		case 0:
			return "Owner";
		case OrganizationRole.ADMIN:
		case 10:
			return "Admin";
		case OrganizationRole.MEMBER:
		case 20:
			return "Member";
		case OrganizationRole.VIEWER:
		case 30:
			return "Viewer";
		default:
			return "Unknown";
	}
};

export const getRoleColor = (
	role: OrganizationRole | string | number
): string => {
	// Handle string and numeric role values
	const roleValue = typeof role === "string" ? parseInt(role) : role;

	switch (roleValue) {
		case OrganizationRole.OWNER:
		case 0:
			return "#d32f2f"; // Red
		case OrganizationRole.ADMIN:
		case 10:
			return "#1976d2"; // Blue
		case OrganizationRole.MEMBER:
		case 20:
			return "#388e3c"; // Green
		case OrganizationRole.VIEWER:
		case 30:
			return "#f57c00"; // Orange
		default:
			return "#757575"; // Gray
	}
};

export const getInvitationStatusLabel = (
	status: InvitationStatus | string | number
): string => {
	// Handle string and numeric status values
	const statusValue = typeof status === "string" ? parseInt(status) : status;

	switch (statusValue) {
		case InvitationStatus.PENDING:
		case 0:
			return "Pending";
		case InvitationStatus.ACCEPTED:
		case 1:
			return "Accepted";
		case InvitationStatus.DECLINED:
		case 2:
			return "Declined";
		case InvitationStatus.EXPIRED:
		case 3:
			return "Expired";
		default:
			return "Unknown";
	}
};

// Helper function to safely compare roles
export const isRoleEqual = (
	role1: OrganizationRole | string | number,
	role2: OrganizationRole | string | number
): boolean => {
	const value1 = typeof role1 === "string" ? parseInt(role1) : role1;
	const value2 = typeof role2 === "string" ? parseInt(role2) : role2;
	return value1 === value2;
};

// Helper function to safely check if role is greater than or equal
export const isRoleGreaterOrEqual = (
	role1: OrganizationRole | string | number,
	role2: OrganizationRole | string | number
): boolean => {
	const value1 = typeof role1 === "string" ? parseInt(role1) : role1;
	const value2 = typeof role2 === "string" ? parseInt(role2) : role2;
	return value1 >= value2;
};
